import { useState, useCallback } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';

export function ExportDialog({
  open,
  onOpenChange,
  currentCategoryId,
  currentPriorityId,
  currentSearch,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentCategoryId?: string | null;
  currentPriorityId?: string | null;
  currentSearch?: string;
}) {
  const [format, setFormat] = useState<'vcf' | 'csv'>('vcf');
  const [scope, setScope] = useState<'all' | 'current'>('all');
  const [exporting, setExporting] = useState(false);

  const hasFilters = !!(currentCategoryId || currentPriorityId || currentSearch);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const options =
        scope === 'current' && hasFilters
          ? {
              categoryId: currentCategoryId || undefined,
              priorityId: currentPriorityId || undefined,
              search: currentSearch || undefined,
            }
          : undefined;

      const content = await api.exportContacts(format, options);

      // Trigger download
      const mimeType = format === 'vcf' ? 'text/vcard' : 'text/csv';
      const ext = format === 'vcf' ? 'vcf' : 'csv';
      const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({ description: `Contacts exported as ${ext.toUpperCase()}.` });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Export failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  }, [
    format,
    scope,
    hasFilters,
    currentCategoryId,
    currentPriorityId,
    currentSearch,
    onOpenChange,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Export Contacts</DialogTitle>
          <DialogDescription>Download your contacts as a file.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Format</Label>
            <Select value={format} onValueChange={(v) => setFormat(v as 'vcf' | 'csv')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vcf">vCard (.vcf)</SelectItem>
                <SelectItem value="csv">CSV (.csv)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {hasFilters && (
            <div className="space-y-2">
              <Label>Scope</Label>
              <Select value={scope} onValueChange={(v) => setScope(v as 'all' | 'current')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All contacts</SelectItem>
                  <SelectItem value="current">Current view only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={exporting}>
            {exporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Export
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
